import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Printer } from "lucide-react";
import { Input } from "@/components/ui/input";
import { adminApi, inr, PAY_METHOD } from "@/lib/api";
import { Btn } from "@/admin/ui";

const todayIst = () => new Date(Date.now() + 19800000).toISOString().slice(0, 10);

const collectLine = (o) => {
  if (o.payment_method !== "bank_transfer") return { label: `COLLECT ${inr(o.amount_due)}`, sub: PAY_METHOD[o.payment_method], tone: "bg-black text-white" };
  if (o.payment_status === "verified") return { label: "PAID · COLLECT ₹0", sub: "Bank transfer verified", tone: "border-2 border-black" };
  return { label: "PAYMENT NOT VERIFIED", sub: "Do not hand over. Call the store.", tone: "border-2 border-dashed border-black" };
};

const Slip = ({ o, store }) => {
  const c = collectLine(o);
  return (
    <article className="bg-white text-black border border-stone-300 rounded-md p-6 break-after-page print:border-0 print:rounded-none print:p-0 print:mb-0 mb-6" data-testid={`packing-slip-${o.id}`}>
      <header className="flex items-start justify-between gap-4 border-b border-black pb-3">
        <div className="flex items-center gap-3">
          <img src="/images/logo.jpg" alt="" className="w-12 h-12 rounded-full object-cover" />
          <div>
            <p className="font-display text-2xl font-semibold leading-none">Mukti Fresh Vegetable</p>
            <p className="text-xs mt-1">+91 {store?.support_phone} · {store?.store_address}</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-xl font-extrabold" data-testid={`slip-order-number-${o.id}`}>{o.order_number}</p>
          <p className="text-xs">{o.delivery_date} · {o.slot_label}</p>
        </div>
      </header>

      <section className="grid grid-cols-2 gap-4 py-3 border-b border-black text-sm">
        <div>
          <p className="text-[10px] uppercase font-bold tracking-wider">Deliver to</p>
          <p className="text-lg font-bold" data-testid={`slip-name-${o.id}`}>{o.name}</p>
          <p className="font-semibold">+91 {o.phone}</p>
          <p data-testid={`slip-address-${o.id}`}>{o.address.line1}{o.address.landmark ? `, ${o.address.landmark}` : ""}</p>
          <p>{o.area_name} · {o.pincode}</p>
          {o.customer_note && <p className="mt-1 italic">Note: {o.customer_note}</p>}
        </div>
        <div className={`self-start rounded-md p-3 text-center [print-color-adjust:exact] [-webkit-print-color-adjust:exact] ${c.tone}`} data-testid={`slip-collect-${o.id}`}>
          <p className="text-xl font-extrabold">{c.label}</p>
          <p className="text-xs">{c.sub}</p>
        </div>
      </section>

      <table className="w-full text-sm mt-2">
        <thead><tr className="text-left text-[10px] uppercase tracking-wider border-b border-black"><th className="py-1 w-6">✓</th><th>Item</th><th className="text-right">Qty</th><th className="text-right">Rate</th><th className="text-right">Amount</th></tr></thead>
        <tbody>
          {o.items.map((it, i) => (
            <tr key={i} className={`border-b border-stone-200 ${it.status === "unavailable" ? "line-through text-stone-500" : ""}`} data-testid={`slip-item-${o.id}-${i}`}>
              <td className="py-1.5"><span className="inline-block w-3.5 h-3.5 border border-black" /></td>
              <td>{it.name}{it.cut ? ` (${it.cut})` : ""} <span className="text-xs">· {it.unit}</span>{it.status === "unavailable" && <b className="no-underline ml-1">REMOVED</b>}</td>
              <td className="text-right font-bold">{it.qty}</td>
              <td className="text-right">{inr(it.price)}</td>
              <td className="text-right">{inr(it.line_total)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="mt-2 ml-auto w-64 text-sm space-y-0.5">
        <div className="flex justify-between"><span>Subtotal</span><span>{inr(o.subtotal)}</span></div>
        <div className="flex justify-between"><span>Delivery fee</span><span>{inr(o.delivery_fee)}</span></div>
        {o.refund_total > 0 && <div className="flex justify-between"><span>Removed items</span><span>− {inr(o.refund_total)}</span></div>}
        <div className="flex justify-between font-extrabold text-base border-t border-black pt-1"><span>Total</span><span>{inr(o.amount_due)}</span></div>
      </div>
      <p className="text-[10px] mt-4">No substitutions. Unavailable items are removed and refunded the same day. Thank you for shopping with us!</p>
    </article>
  );
};

export default function AdminPackingSlips() {
  const [params, setParams] = useSearchParams();
  const id = params.get("id");
  const date = params.get("date") || todayIst();
  const [orders, setOrders] = useState(null);
  const [store, setStore] = useState(null);

  useEffect(() => { adminApi.get("/settings").then((r) => setStore(r.data)); }, []);
  useEffect(() => {
    const req = id ? adminApi.get(`/orders/${id}`).then((r) => [r.data]) : adminApi.get("/orders", { params: { date } }).then((r) => r.data.filter((o) => o.status !== "cancelled"));
    req.then((list) => setOrders([...list].sort((a, b) => a.slot_label.localeCompare(b.slot_label) || a.order_number.localeCompare(b.order_number))));
  }, [id, date]);

  return (
    <div className="max-w-3xl">
      <div className="flex flex-wrap items-center gap-2 mb-4 print:hidden" data-testid="slips-toolbar">
        <h1 className="font-display text-3xl text-primary font-semibold mr-auto">Packing slips</h1>
        {!id && <Input type="date" value={date} onChange={(e) => setParams({ date: e.target.value })} className="h-9 w-40 bg-white" data-testid="slips-date-input" />}
        <Btn onClick={() => window.print()} disabled={!orders?.length} data-testid="slips-print-btn"><Printer className="w-4 h-4 inline mr-1" />Print {orders?.length || 0}</Btn>
      </div>
      {orders && orders.length === 0 && <p className="text-sm text-stone-500 print:hidden" data-testid="slips-empty">No orders for {date}.</p>}
      {orders?.map((o) => <Slip key={o.id} o={o} store={store} />)}
    </div>
  );
}
